import { createContext, use, useCallback, useEffect, useState, type ReactNode } from 'react';
import { api, FalhaDaApi, registrarPerdaDeSessao } from '../api/cliente';

export interface Colaboradora {
  readonly id: string;
  readonly nome: string;
  readonly login: string;
  readonly papel: 'ADVOGADA' | 'ASSISTENTE';
}

interface Sessao {
  readonly usuaria: Colaboradora | null;
  readonly carregando: boolean;
  entrar(login: string, senha: string): Promise<void>;
  sair(): Promise<void>;
}

const Contexto = createContext<Sessao | null>(null);

export function SessaoProvedor({ children }: { children: ReactNode }) {
  const [usuaria, setUsuaria] = useState<Colaboradora | null>(null);
  const [carregando, setCarregando] = useState(true);

  /**
   * Ao abrir a página, tenta restaurar a sessão.
   *
   * Primeiro pergunta quem é; se o token de acesso (30 min) já expirou, tenta
   * renovar com o de renovação (30 dias) antes de desistir. É o que faz a
   * colaboradora não ser jogada para o login toda meia hora.
   */
  useEffect(() => {
    let ativo = true;

    async function restaurar() {
      try {
        const r = await api.buscar<{ usuario: Colaboradora }>('/autenticacao/eu');
        if (ativo) setUsuaria(r.usuario);
      } catch (erro) {
        if (erro instanceof FalhaDaApi && erro.exigeLogin) {
          try {
            const r = await api.criar<{ usuario: Colaboradora }>('/autenticacao/renovar');
            if (ativo) setUsuaria(r.usuario);
          } catch {
            if (ativo) setUsuaria(null);
          }
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    void restaurar();
    return () => {
      ativo = false;
    };
  }, []);

  /**
   * Sessão perdida de vez — renovação tentada e recusada.
   *
   * Sem isto, o 401 definitivo virava um erro solto no meio da tela e a pessoa
   * continuava preenchendo formulário que não gravava. Zerar a usuária devolve o
   * login, que é a única coisa que resolve.
   */
  useEffect(() => {
    registrarPerdaDeSessao(() => setUsuaria(null));
    return () => registrarPerdaDeSessao(null);
  }, []);

  const entrar = useCallback(async (login: string, senha: string) => {
    const r = await api.criar<{ usuario: Colaboradora }>('/autenticacao/entrar', {
      login,
      senha,
    });
    setUsuaria(r.usuario);
  }, []);

  const sair = useCallback(async () => {
    await api.criar('/autenticacao/sair');
    setUsuaria(null);
  }, []);

  return <Contexto value={{ usuaria, carregando, entrar, sair }}>{children}</Contexto>;
}

export function useSessao(): Sessao {
  const contexto = use(Contexto);
  if (!contexto) throw new Error('useSessao precisa estar dentro de <SessaoProvedor>.');
  return contexto;
}
